import React from 'react';
import { TipTapBaseNode, TipTapRender } from './renderer';
import cn from 'classnames';
import { ExternalReactRenderers } from '../Editor/types';
import { useHandlers } from './useHandlers';
import type { ExtensionsConfigOptions } from '../Editor/types';
import { Measureable } from './types';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import isEmpty from 'lodash/isEmpty';
import { getContentFromStorage } from '../utils/tiptap';
import { setRefs } from '@wix/da-ds/pkg/utils/refHelpers';
import { cloneObject } from '../utils/browser';

import baseStyles from './styles/base.scss';

export interface Props extends Measureable {
  contentJson: any;
  className?: string;
  externalRenderers: ExternalReactRenderers;
  extensionConfig: ExtensionsConfigOptions;
  presetId: string;
}

const Viewer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      contentJson,
      className,
      externalRenderers,
      extensionConfig,
      cookieType,
      defaultSize,
      presetId,
    },
    outerRef
  ) => {
    const handlers = useHandlers(extensionConfig);
    const { ref, dimensions } = useMeasureElementCookie(cookieType, {
      cookieDim: 'width',
      defaultSize,
    });

    if (!contentJson) {
      return null;
    }

    // Allow both our full wrapped docs and direct nodes
    let content;
    try {
      content = cloneObject(
        getContentFromStorage(contentJson)
      ) as TipTapBaseNode;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load the editor markup', e);
    }

    if (isEmpty(content)) {
      return null;
    }

    return (
      <div
        data-editor-viewer="1"
        ref={node => {
          setRefs([ref, outerRef], node);
        }}
        className={cn(
          className,
          baseStyles['base-tiptap'],
          baseStyles['never-overflow']
        )}
      >
        <TipTapRender
          presetId={presetId}
          node={content}
          handlers={handlers}
          externalRenderers={externalRenderers}
          extensionConfig={extensionConfig}
          containerWidth={dimensions.width}
        />
      </div>
    );
  }
);

export default Viewer;
